import { createEmptyVideoLayoutModel } from './layoutModelTypes'
import {
  RAIL_STATE_IDS,
  VIDEO_LAYOUT_IDS,
  normalizeRailStateId,
  normalizeVideoLayoutId,
} from './layoutIds'

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function asParticipantList(participants) {
  return Array.isArray(participants) ? participants.filter(Boolean) : []
}

function uniqueParticipantIds(participants = []) {
  return Array.from(
    new Set(
      participants
        .map((participant) => normalizeOptionalString(participant?.id))
        .filter(Boolean),
    ),
  )
}

function resolvePrimaryParticipantId({
  visibleParticipants,
  dominantParticipantId,
  manualDominantParticipantId,
}) {
  const visibleParticipantIds = uniqueParticipantIds(visibleParticipants)

  if (manualDominantParticipantId && visibleParticipantIds.includes(manualDominantParticipantId)) {
    return manualDominantParticipantId
  }

  if (dominantParticipantId && visibleParticipantIds.includes(dominantParticipantId)) {
    return dominantParticipantId
  }

  return visibleParticipantIds[0] || null
}

function resolveLocalParticipantId(visibleParticipants = [], localParticipantId = null) {
  if (localParticipantId) {
    return localParticipantId
  }

  return (
    visibleParticipants.find((participant) => participant?.isLocal === true)?.id || null
  )
}

function resolveScreenShareParticipantId(visibleParticipants = []) {
  return (
    visibleParticipants.find((participant) => participant?.screenSharing === true)?.id || null
  )
}

function buildAvailableRailStateIds(surfacePolicy = {}, hasRail) {
  if (!hasRail) {
    return []
  }

  const allowedRailStateIds = Array.isArray(surfacePolicy?.allowedRailStateIds)
    ? surfacePolicy.allowedRailStateIds
    : Object.values(RAIL_STATE_IDS)

  return allowedRailStateIds
    .map((railStateId) => normalizeRailStateId(railStateId, null))
    .filter(Boolean)
}

/**
 * Builds a provider-neutral layout model that can be rendered by any provider binder.
 *
 * @param {object} [options={}]
 * @returns {import('./layoutModelTypes').VideoLayoutModel}
 */
export function buildVideoLayoutModel(options = {}) {
  const participants = asParticipantList(options?.participants)
  const visibleParticipants = asParticipantList(options?.visibleParticipants)
  const visibleParticipantIds = uniqueParticipantIds(visibleParticipants)
  const surfacePolicy = options?.surfacePolicy || {}
  const screenShareParticipantId = resolveScreenShareParticipantId(visibleParticipants)
  const promoteScreenShareToHero =
    Boolean(screenShareParticipantId) && surfacePolicy?.promoteScreenShareToHero !== false
  const hiddenParticipantIds = participants
    .map((participant) => normalizeOptionalString(participant?.id))
    .filter((participantId) => participantId && !visibleParticipantIds.includes(participantId))
  const layoutId = promoteScreenShareToHero
    ? VIDEO_LAYOUT_IDS.stageOverlay
    : normalizeVideoLayoutId(options?.layoutId)
  const primaryParticipantId = resolvePrimaryParticipantId({
    visibleParticipants,
    dominantParticipantId:
      promoteScreenShareToHero
        ? screenShareParticipantId
        : normalizeOptionalString(options?.dominantParticipantId),
    manualDominantParticipantId: normalizeOptionalString(
      options?.manualDominantParticipantId,
    ),
  })
  const localVisibleParticipantId = resolveLocalParticipantId(
    visibleParticipants,
    normalizeOptionalString(options?.localParticipantId),
  )
  const controlsOverlayMode =
    normalizeOptionalString(surfacePolicy?.controlsOverlayMode, 'overlay') || 'overlay'
  const useFullVideoRegion = surfacePolicy?.useFullVideoRegion !== false
  const baseModel = createEmptyVideoLayoutModel({
    layoutId,
    railStateId: promoteScreenShareToHero
      ? RAIL_STATE_IDS.stacked
      : normalizeRailStateId(
          options?.railStateId,
          surfacePolicy?.defaultRailStateId || RAIL_STATE_IDS.stacked,
        ),
    controlsOverlayMode,
    useFullVideoRegion,
    screenShareHeroMode: promoteScreenShareToHero,
  })

  if (promoteScreenShareToHero) {
    const railParticipantIds = visibleParticipantIds.filter(
      (participantId) => participantId !== screenShareParticipantId,
    )

    return {
      ...baseModel,
      primaryParticipantId: screenShareParticipantId,
      railParticipantIds,
      selfPreviewParticipantId: null,
      screenShareParticipantId,
      hiddenParticipantIds,
      availableRailStateIds: railParticipantIds.length
        ? [RAIL_STATE_IDS.stacked]
        : [],
      hasRail: railParticipantIds.length > 0,
    }
  }

  if (layoutId === VIDEO_LAYOUT_IDS.grid) {
    return {
      ...baseModel,
      gridParticipantIds: visibleParticipantIds,
      screenShareParticipantId,
      hiddenParticipantIds,
    }
  }

  const auxiliaryParticipantIds = visibleParticipantIds.filter(
    (participantId) => participantId !== primaryParticipantId,
  )

  if (layoutId === VIDEO_LAYOUT_IDS.split) {
    if (auxiliaryParticipantIds.length === 0) {
      return {
        ...baseModel,
        primaryParticipantId,
        screenShareParticipantId,
        hiddenParticipantIds,
      }
    }

    if (auxiliaryParticipantIds.length === 1) {
      return {
        ...baseModel,
        primaryParticipantId,
        secondaryParticipantIds: auxiliaryParticipantIds,
        screenShareParticipantId,
        hiddenParticipantIds,
      }
    }

    return {
      ...baseModel,
      primaryParticipantId,
      secondaryParticipantIds: auxiliaryParticipantIds.slice(0, 1),
      gridParticipantIds: auxiliaryParticipantIds.slice(1),
      screenShareParticipantId,
      hiddenParticipantIds,
    }
  }

  const allowSelfPreviewPip = surfacePolicy?.allowSelfPreviewPip !== false
  const selfPreviewParticipantId =
    allowSelfPreviewPip &&
    visibleParticipantIds.length > 1 &&
    localVisibleParticipantId &&
    localVisibleParticipantId !== primaryParticipantId
      ? localVisibleParticipantId
      : null
  const railParticipantIds = auxiliaryParticipantIds.filter(
    (participantId) => participantId !== selfPreviewParticipantId,
  )
  const hasRail = railParticipantIds.length > 0

  return {
    ...baseModel,
    primaryParticipantId,
    railParticipantIds,
    selfPreviewParticipantId,
    screenShareParticipantId,
    hiddenParticipantIds,
    availableRailStateIds: buildAvailableRailStateIds(surfacePolicy, hasRail),
    hasRail,
  }
}
